import React, { useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  IconButton,
  Popover,
  Button,
} from "@material-ui/core";
import SettingsIcon from "@material-ui/icons/Settings";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import DashportLogo from "../../../assets/dashport-logos/DASHPORT_color_horizontal.png";

const STORAGE_KEY = "dashport_winnersBoard_columnas";
const OPCIONES_COLUMNAS = [1, 2, 3, 4, 6];
const MAX_CARACTERES_NOMBRE = 24;
const COLUMNAS_DESDE_LAS_QUE_SE_ACORTA = 4;

function acortarNombre(nombre, columnasPorFila) {
  if (!nombre) return nombre;
  if (columnasPorFila < COLUMNAS_DESDE_LAS_QUE_SE_ACORTA) return nombre;
  if (nombre.length <= MAX_CARACTERES_NOMBRE) return nombre;
  return nombre.substring(0, MAX_CARACTERES_NOMBRE - 1).trimEnd() + "…";
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 20,
    backgroundColor: "#f4f4f4",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 15,
  },
  logo: {
    height: 40,
  },
  modalidadPaper: {
    padding: 10,
    marginBottom: 15,
  },
  modalidadTitulo: {
    fontWeight: "bold",
    marginBottom: 8,
    color: "#333",
  },
  categoriasGrid: {
    // Flexbox en vez de CSS Grid: html2canvas (usado para exportar a PDF)
    // no soporta bien "display: grid" y termina colapsando el layout a una
    // sola caja en la captura.
    display: "flex",
    flexWrap: "wrap",
  },
  categoriaBox: {
    boxSizing: "border-box",
    minWidth: 0,
    border: "1px solid #ddd",
    borderRadius: 6,
    padding: 6,
    margin: 3,
    backgroundColor: "#ffffff",
  },
  categoriaTitulo: {
    fontWeight: "bold",
    marginBottom: 4,
    color: "#c62828",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  ganadorRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 0",
    fontSize: 12,
  },
  ganadorNombre: {
    flexGrow: 1,
    minWidth: 0,
    fontSize: 11,
    display: "flex",
    alignItems: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  posicion: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    fontSize: 10,
    color: "#fff",
    flexShrink: 0,
  },
  popoverContent: {
    padding: 15,
  },
}));

function colorPorPosicion(posicion) {
  if (posicion === 1) return "#c9a227";
  if (posicion === 2) return "#9e9e9e";
  if (posicion === 3) return "#a05a2c";
  return "#607d8b";
}

function leerColumnasGuardadas() {
  try {
    const guardado = window.localStorage.getItem(STORAGE_KEY);
    const numero = parseInt(guardado, 10);
    if (OPCIONES_COLUMNAS.indexOf(numero) >= 0) return numero;
  } catch (e) {
    console.log("No se pudo leer la preferencia de columnas:", e);
  }
  return 3;
}

export default function WinnersBoardCompact({ modalidadGroups, tituloEvento }) {
  const classes = useStyles();
  const [columnasPorFila, setColumnasPorFila] = useState(leerColumnasGuardadas);
  const [anchorEl, setAnchorEl] = useState(null);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const contenidoRef = useRef(null);

  function elegirColumnas(n) {
    setColumnasPorFila(n);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(n));
    } catch (e) {
      console.log("No se pudo guardar la preferencia de columnas:", e);
    }
    setAnchorEl(null);
  }

  async function descargarPDF() {
    if (!contenidoRef.current) return;
    setGenerandoPDF(true);
    // Espera a que React oculte los botones de la barra antes de capturar,
    // para que no salgan dentro del PDF.
    await new Promise((resolve) => setTimeout(resolve, 50));
    try {
      const escala = 2;
      const canvas = await html2canvas(contenidoRef.current, {
        scale: escala,
        backgroundColor: "#f4f4f4",
        useCORS: true,
      });
      const imgData = canvas.toDataURL("image/png");

      // Tamaño lógico real (se retira el factor de escala, que solo se usó
      // para que la captura salga nítida) convertido a mm asumiendo 96 DPI.
      const pxAMm = 25.4 / 96;
      const anchoMm = (canvas.width / escala) * pxAMm;
      const altoMm = (canvas.height / escala) * pxAMm;

      // jsPDF interpreta el array de "format" SIEMPRE en puntos (pt),
      // independientemente del "unit" del documento (su propio pageFormats.a4
      // es [595.28, 841.89], que es A4 en puntos) — hay que convertir mm a pt
      // aquí o la página queda ~2.83x más chica que la imagen y esta se sale
      // de los bordes.
      const mmAPt = 72 / 25.4;
      const pdf = new jsPDF({
        orientation: anchoMm > altoMm ? "landscape" : "portrait",
        unit: "mm",
        format: [anchoMm * mmAPt, altoMm * mmAPt],
      });
      pdf.addImage(imgData, "PNG", 0, 0, anchoMm, altoMm);
      pdf.save((tituloEvento || "resultados") + " - ganadores.pdf");
    } catch (e) {
      console.log("Error al generar el PDF de la vista compacta:", e);
    } finally {
      setGenerandoPDF(false);
    }
  }

  const grupos = modalidadGroups || [];

  return (
    <Paper className={classes.root} ref={contenidoRef}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <img src={DashportLogo} alt="Dashport" className={classes.logo} />
          <Typography variant="h5">{tituloEvento || "Resultados"}</Typography>
        </div>
        {generandoPDF ? (
          <Typography variant="body2">Generando PDF...</Typography>
        ) : (
          <div>
            <IconButton onClick={descargarPDF} title="Descargar PDF">
              <PictureAsPdfIcon />
            </IconButton>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              <SettingsIcon />
            </IconButton>
          </div>
        )}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <div className={classes.popoverContent}>
            <Typography variant="body2" gutterBottom>
              Categorías por fila
            </Typography>
            {OPCIONES_COLUMNAS.map((n) => (
              <Button
                key={n}
                size="small"
                variant={columnasPorFila === n ? "contained" : "outlined"}
                color="primary"
                style={{ margin: 3, minWidth: 36 }}
                onClick={() => elegirColumnas(n)}
              >
                {n}
              </Button>
            ))}
          </div>
        </Popover>
      </div>

      {grupos.length === 0 && (
        <Typography variant="body1">Aún no hay ganadores para mostrar.</Typography>
      )}

      {grupos.map((modalidad) => (
        <Paper key={modalidad.codMod} className={classes.modalidadPaper}>
          <Typography variant="h6" className={classes.modalidadTitulo}>
            {modalidad.descripcionModalidad}
          </Typography>
          <div className={classes.categoriasGrid}>
            {modalidad.categorias.map((categoria) => (
              <div
                key={categoria.cat + "|" + categoria.gen}
                className={classes.categoriaBox}
                style={{ width: "calc(" + 100 / columnasPorFila + "% - 6px)" }}
              >
                <Typography variant="subtitle2" className={classes.categoriaTitulo}>
                  {categoria.cat} — {categoria.generoLabel}
                </Typography>
                {categoria.ganadores.map((ganador, i) => (
                  <div key={i} className={classes.ganadorRow}>
                    <div
                      className={classes.posicion}
                      style={{ backgroundColor: colorPorPosicion(ganador.posicion) }}
                    >
                      {ganador.posicion}
                    </div>
                    <span>#{ganador.num}</span>
                    <span className={classes.ganadorNombre}>
                      {acortarNombre(ganador.nombre, columnasPorFila)}
                    </span>
                    <span>{ganador.tiempo}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Paper>
      ))}
    </Paper>
  );
}
